import { useTranslations } from 'next-intl';
import { useController, useFormContext } from 'react-hook-form';

import { TpSlTriggerTimeIndicatorMapper } from '@flipster/features/src/tpsl-modal/constant';
import { css, cx } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { ControlNumberInput, FormItem } from '@flipster/ui';
import { getCustomFormError } from '@flipster/utils/error';
import { V2OrderSideType } from '@flipster/utils/types/base';

import { TpslTimeTriggerSchema } from '@web/features/modals/TimeTriggerTpslModal/bunja/validation';

const addComma = (value: string) => {
  const [integer, decimal] = value.split('.');
  const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimal ? `${formattedInteger}.${decimal}` : formattedInteger + (value.endsWith('.') ? '.' : '');
};

interface Props {
  precision: number;
  tpSlType: 'tp' | 'sl';
  side?: V2OrderSideType;
}

const TriggerPriceRatioInput = ({ precision, side, tpSlType }: Props) => {
  const transTrade = useTranslations('Trade.r250401');
  const transAsset = useTranslations('Assets.r250401');
  const {
    control,
    formState: { errors },
  } = useFormContext<TpslTimeTriggerSchema>();
  const {
    field: { value },
  } = useController({ name: 'triggerPriceRatio', control });

  if (!side) return null;

  return (
    <div className={css({ py: 4, px: 16, '& label': { textStyle: 'Text/S/Regular' } })}>
      <FormItem
        label={transTrade('Trigger_when_price_moves')}
        error={getCustomFormError(errors.triggerPriceRatio)}
        inheritLabel
      >
        <div className={flex({ position: 'relative', align: 'center', gap: 6 })}>
          <span className={css({ w: 16, textStyle: 'Title/M', color: 'content.default.level-5' })}>
            {TpSlTriggerTimeIndicatorMapper[side][tpSlType]}
          </span>
          <ControlNumberInput
            name='triggerPriceRatio'
            shape='inner'
            precision={precision}
            inputSize={7}
            max='9999.99'
            className={sectionNumberInput}
          />
          <div className={sectionFakeInput}>
            {value && (
              <>
                <span className={cx(sectionInputNumber, css({ color: 'content.default.level-5' }))}>
                  {addComma(value)}%
                </span>
                <span className={sectionDefaultText}>{transAsset('From_entry_price')}</span>
              </>
            )}
            {!value && (
              <>
                <span className={cx(sectionInputNumber, css({ color: 'content.default.level-1' }))}>
                  {transTrade('Enter')} %
                </span>
                <span className={sectionDefaultText}>{transAsset('From_entry_price')}</span>
              </>
            )}
          </div>
        </div>
      </FormItem>
    </div>
  );
};

export default TriggerPriceRatioInput;

const sectionNumberInput = css({
  '& > input': {
    p: '0!',
    textStyle: 'Title/M!',
    color: 'transparent!',
    caretColor: 'content.accent.info',
  },
});

const sectionFakeInput = css({
  position: 'absolute',
  top: 2,
  left: 22,
  pointerEvents: 'none',
  userSelect: 'none',
  fontVariantNumeric: 'tabular-nums', // 기존 number input과 동일하게 적용
});

const sectionDefaultText = css({
  textStyle: 'Text/M/Regular',
  color: 'content.default.level-5',
});

const sectionInputNumber = css({
  textStyle: 'Title/M',
  mr: 4,
});
